import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

function ItemSearchBox({ value, onSelect, itemMaster, placeholder }) {
  const [query, setQuery] = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  const search = (q) => {
    setQuery(q);
    if (!q || q.length < 1) { setResults([]); setOpen(false); return; }
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const r = itemMaster.filter(i => {
      const hay = ((i.item_code||'') + ' ' + (i.item_name||'')).toLowerCase();
      return words.every(w => hay.includes(w));
    }).slice(0, 30);
    setResults(r);
    setOpen(r.length > 0);
  };

  const select = (item) => {
    onSelect(item);
    setQuery(item.item_code + ' — ' + item.item_name);
    setResults([]);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input type="text" value={query} onChange={e => search(e.target.value)}
        placeholder={placeholder || 'Search item...'}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        style={{ width: '100%', fontSize: 11, padding: '4px 6px' }} />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: 6, maxHeight: 160, overflowY: 'auto', zIndex: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
          {results.map(i => (
            <div key={i.id} onMouseDown={() => select(i)}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background='white'}>
              <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{i.item_code}</span> — {i.item_name}
              <span style={{ float: 'right', color: 'var(--text-muted)', fontSize: 10 }}>{i.unit} · AED {i.unit_cost}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PhotoTrialUpload({ user }) {
  const [step, setStep] = useState('list');
  const [uploads, setUploads] = useState([]);
  const [preview, setPreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [editData, setEditData] = useState(null);
  const [products, setProducts] = useState([]);
  const [itemMaster, setItemMaster] = useState([]);
  const [approving, setApproving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [filter, setFilter] = useState('all');
  const fileRef = useRef();
  const cameraRef = useRef();

  useEffect(() => {
    loadUploads();
    API.get('/products').then(r => setProducts(r.data)).catch(() => {});
    API.get('/items').then(r => setItemMaster(r.data)).catch(() => {});
  }, []);

  const loadUploads = () => {
    API.get('/photo-uploads').then(r => setUploads(r.data)).catch(() => {});
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      setImageBase64(e.target.result.split(',')[1]);
      setStep('extract');
    };
    reader.readAsDataURL(file);
  };

  const handleExtract = async () => {
    if (!imageBase64) return;
    setExtracting(true);
    try {
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
      const r = await fetch(base + '/photo-trial-extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: imageBase64 })
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setExtracted(data.extracted);
      setStep('confirm');
    } catch(e) {
      toast.error('Extraction failed: ' + e.message);
    }
    setExtracting(false);
  };

  const handleConfirmSave = async () => {
    try {
      await API.post('/photo-uploads/save', {
        raw_json: JSON.stringify(extracted),
        sheet_type: extracted.sheet_type || 'trial',
        project_code: extracted.project_code || '',
        product_code: extracted.product_code || '',
        material_count: extracted.materials?.length || 0,
        trial_count: extracted.trials?.filter(t => t.date)?.length || 0,
      });
      toast.success('Saved to pending list!');
      setStep('list');
      setPreview(null);
      setImageBase64(null);
      setExtracted(null);
      loadUploads();
    } catch(e) {
      toast.error('Save failed: ' + e.message);
    }
  };

  const openReview = (upload) => {
    const data = JSON.parse(upload.raw_json || '{}');
    setEditData({
      ...data,
      upload_id: upload.id,
      selected_product: upload.product_id || '',
      materials: (data.materials || []).map(m => ({ ...m, _skip: false }))
    });
    setReviewing(upload);
  };

  const updateMaterial = (idx, field, value) => {
    setEditData(prev => ({
      ...prev,
      materials: prev.materials.map((m, i) => i === idx ? { ...m, [field]: value } : m)
    }));
  };

  const selectItem = (idx, item) => {
    setEditData(prev => ({
      ...prev,
      materials: prev.materials.map((m, i) => i === idx ? { ...m, item_code: item.item_code, item_name: item.item_name, unit: item.unit || m.unit } : m)
    }));
  };

  const handleApprove = async () => {
    if (!editData.selected_product) { toast.error('Please select a product'); return; }
    setApproving(true);
    try {
      // Filter out skipped materials
      const materials = editData.materials.filter(m => !m._skip);
      await API.post('/photo-trial-create', {
        product_id: editData.selected_product,
        trials: editData.trials,
        trial_number: editData.trial_number,
        date: editData.date,
        materials,
        upload_id: editData.upload_id
      });
      // Update upload status to approved
      await API.put('/photo-uploads/' + editData.upload_id, { status: 'approved' });
      toast.success('Trial created successfully!');
      setReviewing(null);
      setEditData(null);
      loadUploads();
    } catch(e) {
      toast.error('Failed: ' + e.message);
    }
    setApproving(false);
  };

  const handleReject = async (uploadId) => {
    if (!window.confirm('Reject this upload?')) return;
    await API.put('/photo-uploads/' + uploadId, { status: 'rejected' });
    setReviewing(null);
    setEditData(null);
    loadUploads();
    toast.success('Upload rejected');
  };

  const STATUS_COLORS = {
    pending: { bg: '#fff3e0', color: '#e67e00' },
    approved: { bg: '#e8f5e9', color: '#2e7d32' },
    rejected: { bg: '#fce4ec', color: '#c62828' },
    extracted: { bg: '#e3f2fd', color: '#1565c0' },
  };

  const filtered = uploads.filter(u => filter === 'all' || u.status === filter);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Photo Trial Upload</div>
          <div className="page-subtitle">Upload, review and approve trial sheets</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => fileRef.current.click()}>📁 Upload</button>
          <button className="btn btn-primary" onClick={() => cameraRef.current.click()}>📷 Camera</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={e => handleFile(e.target.files[0])} style={{ display: 'none' }} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={e => handleFile(e.target.files[0])} style={{ display: 'none' }} />
      </div>

      {/* Sheet download links */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <a href="/trial-sheet.html" target="_blank" className="btn btn-secondary btn-sm">📄 Trial Sheet</a>
        <a href="/sample-prep-sheet.html" target="_blank" className="btn btn-secondary btn-sm">🧪 Sample Prep Sheet</a>
        <a href="/multi-trial-sheet.html" target="_blank" className="btn btn-secondary btn-sm">🧾 Multi-Trial Sheet (1/4/6 per page)</a>
      </div>


      {/* Confirm step */}
      {step === 'confirm' && extracted && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="card" style={{ padding: 12 }}>
            <img src={preview} alt="Sheet" style={{ width: '100%', borderRadius: 6 }} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: 'var(--accent)' }}>✅ Extraction Complete — Please Verify</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              <div style={{ background: 'var(--bg-elevated)', padding: '8px 12px', borderRadius: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Project Code</div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{extracted.project_code || '—'}</div>
              </div>
              <div style={{ background: 'var(--bg-elevated)', padding: '8px 12px', borderRadius: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Product Code</div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: 'var(--accent)' }}>{extracted.product_code || '—'}</div>
              </div>
            </div>

            {extracted.trials && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {extracted.trials.map((t, i) => (
                  <div key={i} style={{ background: '#f3e8ff', color: 'var(--accent)', padding: '4px 10px', borderRadius: 6, fontSize: 12 }}>
                    T-{t.trial_number} {t.date && <span style={{ color: 'var(--text-muted)' }}>· {t.date}</span>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{extracted.materials?.length || 0} items detected:</div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-elevated)' }}>
                    <th style={{ padding: '6px 10px', fontSize: 11, textAlign: 'left' }}>Item Name</th>
                    {extracted.trials ? extracted.trials.slice(0,3).map((t,i) => (
                      <th key={i} style={{ padding: '6px 6px', fontSize: 11, textAlign: 'center', color: 'var(--accent)' }}>T-{t.trial_number}</th>
                    )) : <th style={{ padding: '6px 10px', fontSize: 11, textAlign: 'right' }}>Qty</th>}
                  </tr>
                </thead>
                <tbody>
                  {extracted.materials?.map((m, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 10px', fontSize: 12 }}>{m.item_name || m.item_code || '—'}</td>
                      {extracted.trials ? extracted.trials.slice(0,3).map((t,ti) => (
                        <td key={ti} style={{ padding: '5px 6px', fontSize: 12, textAlign: 'center', color: '#555' }}>{m.quantities?.[ti] ?? '—'}</td>
                      )) : <td style={{ padding: '5px 10px', fontSize: 12, textAlign: 'right' }}>{m.quantity}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              If the data looks correct, confirm to save to pending list. Otherwise re-upload.
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmSave}>
                ✅ Confirm & Save to Pending
              </button>
              <button className="btn btn-secondary" onClick={() => { setStep('extract'); setExtracted(null); }}>
                Re-upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extract step */}
      {step === 'extract' && preview && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="card" style={{ padding: 12 }}>
            <img src={preview} alt="Sheet" style={{ width: '100%', borderRadius: 6 }} />
          </div>
          <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Ready to Extract</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>AI will read the sheet and save to pending list for review</div>
            <button className="btn btn-primary" onClick={handleExtract} disabled={extracting}>
              {extracting ? '⏳ Reading sheet...' : '🔍 Extract & Save to Pending'}
            </button>
            <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => { setStep('list'); setPreview(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Uploads list */}
      {step === 'list' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['all','pending','approved','rejected'].map(s => (
              <button key={s} className={`btn btn-sm ${filter===s?'btn-primary':'btn-secondary'}`} onClick={() => setFilter(s)}>
                {s.charAt(0).toUpperCase()+s.slice(1)} {s!=='all'&&<span style={{ marginLeft: 4 }}>({uploads.filter(u=>u.status===s).length})</span>}
              </button>
            ))}
          </div>

          <div className="card">
            {filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📷</div>
                <p>No uploads yet. Upload a trial sheet photo to get started.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Project Code</th>
                      <th>Product Code</th>
                      <th>Materials</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => {
                      const sc = STATUS_COLORS[u.status] || { bg: '#f5f5f5', color: '#666' };
                      return (
                        <tr key={u.id}>
                          <td style={{ fontSize: 12 }}>{new Date(u.created_at).toLocaleDateString('en-AE')}</td>
                          <td style={{ fontSize: 12 }}>{u.sheet_type || 'trial'}</td>
                          <td><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{u.project_code || '—'}</span></td>
                          <td><span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{u.product_code || '—'}</span></td>
                          <td style={{ textAlign: 'center', fontSize: 12 }}>{u.material_count || '—'}</td>
                          <td><span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{u.status}</span></td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {(u.status === 'pending' || u.status === 'extracted') && (
                                <button className="btn btn-primary btn-sm" onClick={() => openReview(u)}>Review</button>
                              )}
                              {u.status === 'approved' && (
                                <button className="btn btn-secondary btn-sm" onClick={() => openReview(u)}>View</button>
                              )}
                              {u.status !== 'rejected' && (
                                <button className="btn btn-danger btn-sm" onClick={() => handleReject(u.id)}>Reject</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Review modal */}
      {reviewing && editData && (
        <div className="modal-overlay">
          <div className="modal modal-lg" style={{ maxWidth: 800 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Review Upload — {editData.sheet_type === 'sample_prep' ? 'Sample Prep' : 'Trial Sheet'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{reviewing.project_code} · {reviewing.product_code}</div>
              </div>
              <button className="modal-close" onClick={() => { setReviewing(null); setEditData(null); }}>✕</button>
            </div>

            {/* Product selector */}
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Link to Product *</label>
              <select value={editData.selected_product} onChange={e => setEditData(prev => ({ ...prev, selected_product: e.target.value }))}>
                <option value="">— Select product —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.product_code || '—'})</option>)}
              </select>
            </div>

            {/* Trials */}
            {editData.trials && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                {editData.trials.map((t, i) => (
                  <div key={i} style={{ background: 'var(--bg-elevated)', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: 'var(--accent)' }}>T-{t.trial_number}</span>
                    {t.date && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{t.date}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Materials table */}
            {editData.materials?.length > 0 && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto', marginBottom: 14 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-elevated)' }}>
                      <th style={{ padding: '7px 8px', fontSize: 11, textAlign: 'center', width: 40 }}>✓</th>
                      <th style={{ padding: '7px 8px', fontSize: 11, textAlign: 'left' }}>Item (flexi search)</th>
                      <th style={{ padding: '7px 8px', fontSize: 11, textAlign: 'center', width: 60 }}>UOM</th>
                      {editData.trials ? editData.trials.map((t, i) => (
                        <th key={i} style={{ padding: '7px 6px', fontSize: 11, textAlign: 'center', background: '#f3e8ff', color: 'var(--accent)', width: 60 }}>T-{t.trial_number}</th>
                      )) : <th style={{ padding: '7px 8px', fontSize: 11, textAlign: 'center', width: 70 }}>Qty</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {editData.materials.map((m, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid var(--border)', opacity: m._skip ? 0.4 : 1 }}>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          <input type="checkbox" checked={!m._skip} onChange={e => updateMaterial(idx, '_skip', !e.target.checked)} />
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <ItemSearchBox
                            value={m.item_code ? m.item_code + ' — ' + m.item_name : m.item_name || ''}
                            onSelect={(item) => selectItem(idx, item)}
                            itemMaster={itemMaster}
                            placeholder={m.item_name || 'Search item...'}
                          />
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <select value={m.unit || 'kg'} onChange={e => updateMaterial(idx, 'unit', e.target.value)} style={{ width: 55, fontSize: 11, padding: '3px 2px' }}>
                            <option value="kg">kg</option>
                            <option value="g">g</option>
                            <option value="l">l</option>
                            <option value="ml">ml</option>
                            <option value="nos">nos</option>
                            <option value="pcs">pcs</option>
                          </select>
                        </td>
                        {editData.trials ? editData.trials.map((t, ti) => (
                          <td key={ti} style={{ padding: '4px 6px', textAlign: 'center', fontSize: 12, background: '#faf5fc' }}>
                            {m.quantities?.[ti] ?? '—'}
                          </td>
                        )) : (
                          <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                            <input type="number" value={m.quantity || ''} onChange={e => updateMaterial(idx, 'quantity', e.target.value)} style={{ width: 60, fontSize: 11, padding: '3px 4px', textAlign: 'right' }} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              Uncheck items to skip them. Quantities from sheet are frozen — edit UOM or item as needed.
            </div>

            <div className="modal-footer">
              <button className="btn btn-danger" onClick={() => handleReject(reviewing.id)}>✕ Reject</button>
              <button className="btn btn-secondary" onClick={() => { setReviewing(null); setEditData(null); }}>Close</button>
              {reviewing.status !== 'approved' && (
                <button className="btn btn-primary" onClick={handleApprove} disabled={approving || !editData.selected_product}>
                  {approving ? 'Creating...' : '✅ Approve & Create Trial'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
